import { observer } from "mobx-react-lite";
import { useState } from "react";
import blocksManager from "../../../stores/blocks-manager";
import bodyStylesManager from "../../../stores/body-styles-manager";
import contentManager from "../../../stores/content-manager";
import SatoriDimensionsBox from "../shared/satori-ui/dimensions-box";
import { IHeroSliderBlockData, ISlideImage, IHeroSlide, IHeroContent, ICallToAction } from "../../../types/content-block-data";
import SatoriCheckBox from "../shared/satori-ui/checkbox";
import SatoriDropDownList from "../shared/satori-ui/dropdownlist";
import SatoriSwitch from "../shared/satori-ui/switch";
import SatoriCollapsiblePanel from "../shared/satori-ui/collapsible-panel";
import { Cog, Heading, Image, Paintbrush, SignpostBig, Text, Link, Unlink } from "lucide-react";
import SatoriTextBox from "../shared/satori-ui/textbox";
import SatoriMultiLineBox from "../shared/satori-ui/multilinebox";
import { 
  ACTION_TYPES, 
  NOPERCENT_UNITS, 
  FONT_WEIGHTS, 
  TEXT_DECORATIONS, 
  TEXT_TRANSFORMATIONS, 
  ALIGN_OPTIONS,
  OBJECT_FIT,
  IMAGE_POSITIONS
} from "../../../types/constants";
import SatoriColorPicker from "../shared/satori-ui/colorpicker";
import SatoriButton from "../shared/satori-ui/button";
import { cleanDimenValues } from "../../../lib/page-builder-lib";

const DEFAULT_PLACEHOLDER = "https://placehold.co/600x400?text=NEW";

/**
 * HeroSliderOptions – Sidebar Editing Panel for Hero Slider Blocks
 *
 * Provides a full-featured sidebar panel for configuring all properties of a Hero Slider block
 * in the Satori PageBuilder. Supports per-slide image options, hero content, CTA, and style controls,
 * with real-time MobX-backed updates.
 *
 * @component
 * @category OptionsPanel
 *
 * @example
 * // Render options panel for the currently selected Hero Slider block
 * <HeroSliderOptions />
 *
 * @remarks
 * - Fetches and updates the currently selected Hero Slider block and slide from MobX stores (blocksManager, contentManager).
 * - Renders controls for:
 *   - Slide image (picker, fit, position, alt, description, caption, navigation/action type)
 *   - Carousel style (width, height, border radius, background color)
 *   - Autoplay, loop, navigation arrows, indicators, and autoplay interval
 *   - Per-slide hero header (text, typography, color, spacing, margin, padding, transform)
 *   - Per-slide hero text (rich text, typography, color, spacing, margin, padding, transform)
 *   - Per-slide CTA (button label, URL, open in new tab, alignment)
 * - All field changes call MobX-backed actions for atomic and reactive updates:
 *   - `contentManager.setHeroSliderConfig`
 *   - `blocksManager.setHeroSlideEditing`
 * - Per-field controls support px/rem unit conversion and linked/individual side editing for margin and padding.
 * - Image picker and gallery dialog support is integrated via `blocksManager`.
 * - Mirrors the options panel structure of ImageOptions and other content block editors for a consistent UX.
 * - Fully reactive via MobX's `observer()` wrapper.
 *
 * @see contentManager.setHeroSliderConfig
 * @see blocksManager.setHeroSlideEditing
 * @see ImageOptions, ButtonOptions, ColumnsOptions – For related sidebar editing panels
 */
export default observer(function HeroSliderOptions() {
  // 1. Get selected carousel block and image index from blockManager
  const editing = blocksManager.getBlockEditing();
  const blockId = editing.selectedBlockId;
  const block = blockId ? contentManager.getContentBlockById(blockId) : null;
  const [switchToHeroSlide, setSwitchToHeroSlide] = useState(false);
  if (!block || block.blockType !== "hero_slider") return null;

  const heroSliderData = block.blockData as IHeroSliderBlockData;
  const selectedIndex = blocksManager.getHeroSlideEditing().selectedSlideIndex ?? 0;
  const selectedSlide = heroSliderData.slides[selectedIndex];
  if (!selectedSlide) return (
    <div className="text-gray-500 text-sm px-2 py-4">
      No image selected. Please add or select an image below.
    </div>
  );

  const parentFontSize = bodyStylesManager.getBodyStylesState().typography!["p"].fontSize;
  let parentRawFontSize: number = 0;  // We will always convert this into pixels first (if necessary)
  
  if (parentFontSize?.unit === "rem" || parentFontSize?.unit === "em")
    parentRawFontSize = parseFloat(parentFontSize!.value) * 16;
  else
    parentRawFontSize = parseInt(parentFontSize!.value);

  // 2. Handlers
  const updateImageField = (field: keyof ISlideImage, value: any) => {
    const updatedSlides = heroSliderData.slides.map((slide, i) =>
      i === selectedIndex
        ? {
            ...slide,
            slideImage: {
              ...slide.slideImage,
              [field]: value,
            },
          }
        : slide
    );
    contentManager.setHeroSliderConfig(blockId!, { slides: updatedSlides });
  };

  /** When updating any top-level slide-specific fields. */
  const updateSlideField = (field: keyof IHeroSlide, value: any) => {
    const updatedSlides = heroSliderData.slides.map((slide, i) =>
      i === selectedIndex
        ? {
            ...slide,
            [field]: value,
          }
        : slide
    );
    contentManager.setHeroSliderConfig(blockId!, { slides: updatedSlides });
  };

  /** When updating heroHeader-related fields. */
  const updateHeroHeaderField = (field: keyof IHeroContent, value: any) => {
    const updatedSlides = heroSliderData.slides.map((slide, i) =>
      i === selectedIndex
        ? {
            ...slide,
            heroHeader: {
              ...slide.heroHeader!,  // Start from existing or empty object
              [field]: value,
            },
          }
        : slide
    );
    contentManager.setHeroSliderConfig(blockId!, { slides: updatedSlides });
  };

  /** When updating heroText-related fields. */
  const updateHeroTextField = (field: keyof IHeroContent, value: any) => {
    const updatedSlides = heroSliderData.slides.map((slide, i) =>
      i === selectedIndex
        ? {
            ...slide,
            heroText: {
              ...slide.heroText!,  // Start from existing or empty object
              [field]: value,
            },
          }
        : slide
    );
    contentManager.setHeroSliderConfig(blockId!, { slides: updatedSlides });
  };

  /** When updating cta-related fields. */
  const updateCtaField = (field: keyof ICallToAction, value: any) => {
    const updatedSlides = heroSliderData.slides.map((slide, i) =>
      i === selectedIndex
        ? {
            ...slide,
            cta: {
              ...slide.cta!,  // Start from existing or empty object
              [field]: value,
            },
          }
        : slide
    );
    contentManager.setHeroSliderConfig(blockId!, { slides: updatedSlides });
  };

  const updateHeroSliderField = (field: keyof IHeroSliderBlockData, value: any) => {
    contentManager.setHeroSliderConfig(blockId!, { [field]: value });
  };

  // 3. Panel
  return (
    <section className="flex flex-col">
      {/* 2. Basic Image Options */}
      <SatoriCollapsiblePanel
        icon={<Image size={18} />}
        title={"Image Options"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3">
          {/* Thumbnail preview */}
          <div className="flex items-center p-4 gap-2 justify-between">
            <SatoriButton
              variant={"light"}
              onClick={() => blocksManager.setOpenGalleryDialog(true)}
            >
              Pick an Image
            </SatoriButton>
            <img
              src={selectedSlide.slideImage.src || DEFAULT_PLACEHOLDER}
              alt="Selected"
              className="w-[96px] h-[72px] rounded border object-cover"
            />
          </div>

          {/* Image Fit (mapped to object-fit, or objectFit in React.CSSProperties) */}
          <SatoriDropDownList
            layout="horizontal"
            label={"Image Fit"}
            options={OBJECT_FIT}
            isFontFamily={false}
            value={selectedSlide.slideImage.imageFit || "cover"}
            onChange={(val) => updateImageField("imageFit", val)}
          />

          {/* Image Position (mapped to object-position, or objectPosition in React.CSSProperties) */}
          <SatoriDropDownList
            layout="horizontal"
            label={"Image Position"}
            options={IMAGE_POSITIONS}
            isFontFamily={false}
            value={selectedSlide.slideImage.imagePosition || "center"}
            onChange={(val) => updateImageField("imagePosition", val)}
          />

          <SatoriTextBox
            label={"Alt Text"}
            value={selectedSlide.slideImage.alt || ""}
            onChange={(val) => updateImageField("alt", val)}
            placeholder="Enter an alternate text"
          />

          <SatoriMultiLineBox
            label={"Description"}
            value={selectedSlide.slideImage.description || ""}
            onChange={(val) => updateImageField("description", val)}
            placeholder="Describe this image."
            rows={4}
          />

          <SatoriMultiLineBox
            label={"Caption"}
            value={selectedSlide.slideImage.caption || ""}
            onChange={(val) => updateImageField("caption", val)}
            placeholder="Enter a caption for this image."
            rows={4}
          />
        </div>
      </SatoriCollapsiblePanel>

      {/* 2. Slide Image Action */}
      {!selectedSlide.isHeroMode && (
        <SatoriCollapsiblePanel
          icon={<SignpostBig size={18} />}
          title={"Navigation"}
          defaultOpen={true}
        >
          <div className="flex flex-col gap-3">
            <SatoriDropDownList
              label={"Action Types"}
              options={ACTION_TYPES}
              value={selectedSlide.slideImage.actionType ?? "url"}
              onChange={(val) => updateImageField("actionType", val)}
              isFontFamily={false}
            />
            {/* Conditional UI based on action type */}
            {selectedSlide.slideImage.actionType === "email" ? (
              <SatoriTextBox
                type={"email"}
                value={selectedSlide.slideImage.url ?? ""}
                onChange={(val) => updateImageField("url", val)}
                placeholder="user@email.com"
              />
            ) : selectedSlide.slideImage.actionType === "phone" ? (
              <SatoriTextBox
                type={"tel"}
                value={selectedSlide.slideImage.url ?? ""}
                onChange={(val) => updateImageField("url", val)}
                placeholder="tel:+1234567890"
              />
            ) : (
              <SatoriTextBox
                type={"url"}
                value={selectedSlide.slideImage.url ?? ""}
                onChange={(val) => updateImageField("url", val)}
                placeholder="https://www.example.com"
              />
            )}
            {/* Open in new tab */}
            <SatoriCheckBox
              checked={!!selectedSlide.slideImage.openInNewTab}
              onChange={(val) => updateImageField("openInNewTab", val)}
              label={"Open in new tab"}
            />
          </div>
        </SatoriCollapsiblePanel>
      )}

      {/* --- STYLE FIELDS --- */}
      <SatoriCollapsiblePanel
        icon={<Paintbrush size={18} />}
        title={"Carousel Style"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3">
          <SatoriDimensionsBox
            label={"Width"}
            value={heroSliderData.width?.value || ""}
            unit={heroSliderData.width?.unit || "px"}
            onValueChange={(val) =>
              updateHeroSliderField("width", {
                ...heroSliderData.width,
                value: val,
              })
            }
            onUnitChange={(unit) =>
              updateHeroSliderField("width", { ...heroSliderData.width, unit })
            }
          />

          <SatoriDimensionsBox
            label={"Height"}
            value={heroSliderData.height?.value || ""}
            unit={heroSliderData.height?.unit || "px"}
            onValueChange={(val) =>
              updateHeroSliderField("height", {
                ...heroSliderData.height,
                value: val,
              })
            }
            onUnitChange={(unit) =>
              updateHeroSliderField("height", {
                ...heroSliderData.height,
                unit,
              })
            }
          />

          <SatoriDimensionsBox
            label={"Border Radius"}
            value={heroSliderData.borderRadius?.value || ""}
            unit={heroSliderData.borderRadius?.unit || "px"}
            onValueChange={(val) =>
              updateHeroSliderField("borderRadius", {
                ...heroSliderData.borderRadius,
                value: val,
              })
            }
            onUnitChange={(unit) =>
              updateHeroSliderField("borderRadius", {
                ...heroSliderData.borderRadius,
                unit,
              })
            }
          />

          <SatoriColorPicker
            label={"Background Color"}
            value={heroSliderData.backgroundColor || "#f8fafc"}
            onChange={(val) => updateHeroSliderField("backgroundColor", val)}
          />
        </div>
      </SatoriCollapsiblePanel>

      {/* --- CAROUSEL MODE SETTINGS --- */}
      <div className="flex flex-col pb-4">
        <div className="flex h-16 px-4 items-center">
          <SatoriSwitch
            label={"Enable Hero Mode"}
            checked={selectedSlide.isHeroMode ?? false}
            onChange={(val) => updateSlideField("isHeroMode", val)}
          />
        </div>
        <div className="h-[1px] bg-gray-200" />
        <div className="flex h-16 px-4 items-center">
          <SatoriSwitch
            label={"Autoplay"}
            checked={heroSliderData.autoplay}
            onChange={(val) => updateHeroSliderField("autoplay", val)}
          />
        </div>
        <div className="h-[1px] bg-gray-200" />
        <div className="flex h-16 px-4 items-center">
          <SatoriSwitch
            label={"Loop"}
            checked={heroSliderData.loop}
            onChange={(val) => updateHeroSliderField("loop", val)}
          />
        </div>
        <div className="h-[1px] bg-gray-200" />
        <div className="flex h-16 px-4 items-center">
          <SatoriSwitch
            label={"Show Arrows"}
            checked={heroSliderData.showArrows}
            onChange={(val) => updateHeroSliderField("showArrows", val)}
          />
        </div>
        <div className="h-[1px] bg-gray-200" />
        <div className="flex h-16 px-4 items-center">
          <SatoriSwitch
            label={"Show Indicators"}
            checked={heroSliderData.showIndicators}
            onChange={(val) => updateHeroSliderField("showIndicators", val)}
          />
        </div>
        <div className="h-[1px] bg-gray-200 mb-4" />
        <div className="flex h-16 px-4 items-center">
          <SatoriTextBox
            label={"Autoplay Interval (ms)"}
            value={heroSliderData.autoplayInterval.toString()}
            onChange={(val) =>
              updateHeroSliderField("autoplayInterval", Number(val))
            }
            disabled={!heroSliderData.autoplay}
          />
        </div>
      </div>
      {selectedSlide.isHeroMode && (
        <>
          <SatoriCollapsiblePanel
            icon={<Heading size={18} />}
            title={"Hero Header"}
            defaultOpen={true}
          >
            <div className="flex flex-col gap-3">
              <SatoriTextBox
                label={"Text"}
                value={selectedSlide.heroHeader?.text || ""}
                onChange={(val) => updateHeroHeaderField("text", val)}
              />
              <SatoriDropDownList
                label={"Font Family"}
                isFontFamily={true}
                value={
                  selectedSlide.heroHeader?.fontFamily || "Inter, sans-serif"
                }
                onChange={(val) => updateHeroHeaderField("fontFamily", val)}
              />
              <SatoriDimensionsBox
                label={"Font Size"}
                value={
                  cleanDimenValues(
                    selectedSlide.heroHeader?.fontSize?.value!
                  ) || ""
                }
                unit={selectedSlide.heroHeader?.fontSize?.unit || "px"}
                units={NOPERCENT_UNITS}
                onValueChange={(val) =>
                  updateHeroHeaderField("fontSize", {
                    value: val,
                    unit: selectedSlide.heroHeader?.fontSize?.unit || "rem",
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    updateHeroHeaderField("fontSize", {
                      value:
                        Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.fontSize?.value!
                          ) * parentRawFontSize
                        ).toString() || "",
                      unit: unitVal,
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    updateHeroHeaderField("fontSize", {
                      value:
                        (
                          parseFloat(
                            selectedSlide.heroHeader?.fontSize?.value!
                          ) / parentRawFontSize
                        ).toString() || "",
                      unit: unitVal,
                    });
                  }
                }}
              />

              <SatoriColorPicker
                label={"Text Color"}
                value={selectedSlide.heroHeader?.color || "#ffffff"}
                onChange={(val) => updateHeroHeaderField("color", val)}
              />

              <SatoriDimensionsBox
                label={"Line Height"}
                value={
                  cleanDimenValues(
                    selectedSlide.heroHeader?.lineHeight?.value!
                  ) || ""
                }
                unit={selectedSlide.heroHeader?.lineHeight?.unit || "px"}
                units={NOPERCENT_UNITS}
                onValueChange={(val) =>
                  updateHeroHeaderField("lineHeight", {
                    value: val,
                    unit: selectedSlide.heroHeader?.lineHeight?.unit || "rem",
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    updateHeroHeaderField("lineHeight", {
                      value:
                        Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.lineHeight?.value!
                          ) * parentRawFontSize
                        ).toString() || "",
                      unit: unitVal,
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    updateHeroHeaderField("lineHeight", {
                      value:
                        (
                          parseFloat(
                            selectedSlide.heroHeader?.lineHeight?.value!
                          ) / parentRawFontSize
                        ).toString() || "",
                      unit: unitVal,
                    });
                  }
                }}
              />

              <SatoriDropDownList
                layout={"horizontal"}
                label={"Font Weight"}
                value={selectedSlide.heroHeader?.fontWeight ?? "700"}
                isFontFamily={false}
                options={FONT_WEIGHTS}
                onChange={(val) => updateHeroHeaderField("fontWeight", val)}
              />

              <SatoriDropDownList
                layout={"horizontal"}
                label={"Text Decoration"}
                value={selectedSlide.heroHeader?.textDecoration ?? "none"}
                isFontFamily={false}
                options={TEXT_DECORATIONS}
                onChange={(val) => updateHeroHeaderField("fontWeight", val)}
              />

              <SatoriDropDownList
                label={"Text Transformation"}
                value={selectedSlide.heroHeader?.textTransform ?? "none"}
                isFontFamily={false}
                options={TEXT_TRANSFORMATIONS}
                onChange={(val) => updateHeroHeaderField("textTransform", val)}
              />

              <SatoriDimensionsBox
                label={"Letter Spacing"}
                value={
                  cleanDimenValues(
                    selectedSlide.heroHeader?.letterSpacing?.value!
                  ) || ""
                }
                unit={selectedSlide.heroHeader?.letterSpacing?.unit || "px"}
                units={NOPERCENT_UNITS}
                onValueChange={(val) =>
                  updateHeroHeaderField("letterSpacing", {
                    value: val,
                    unit:
                      selectedSlide.heroHeader?.letterSpacing?.unit || "rem",
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    updateHeroHeaderField("letterSpacing", {
                      value:
                        Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.letterSpacing?.value!
                          ) * parentRawFontSize
                        ).toString() || "",
                      unit: unitVal,
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    updateHeroHeaderField("letterSpacing", {
                      value:
                        (
                          parseFloat(
                            selectedSlide.heroHeader?.letterSpacing?.value!
                          ) / parentRawFontSize
                        ).toString() || "",
                      unit: unitVal,
                    });
                  }
                }}
              />

              {/* Hero Heading Margins */}
              <div className="flex items-center gap-2 my-2 w-full justify-between">
                <span className="text-base">Margins</span>
                <button
                  onClick={() =>
                    updateHeroHeaderField("margin", {
                      ...selectedSlide.heroHeader?.margin,
                      linked: !selectedSlide.heroHeader?.margin?.linked,
                    })
                  }
                  className="ml-1 p-1"
                  title={
                    selectedSlide.heroHeader?.margin?.linked
                      ? "Unlink sides"
                      : "Link all sides"
                  }
                >
                  {selectedSlide.heroHeader?.margin?.linked ? (
                    <Link size={16} />
                  ) : (
                    <Unlink size={16} />
                  )}
                </button>
              </div>
              {selectedSlide.heroHeader?.margin?.linked ? (
                <div className="flex flex-col gap-2">
                  <SatoriDimensionsBox
                    value={
                      cleanDimenValues(
                        selectedSlide.heroHeader?.margin?.left!
                      ) || ""
                    }
                    unit={selectedSlide.heroHeader?.margin?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroHeaderField("margin", {
                        ...selectedSlide.heroHeader?.margin,
                        top: val,
                        right: val,
                        bottom: val,
                        left: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedValue = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.top!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("margin", {
                          top: convertedValue,
                          right: convertedValue,
                          bottom: convertedValue,
                          left: convertedValue,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.margin?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedValue = (
                          parseFloat(selectedSlide.heroHeader?.margin?.top!) /
                          parentRawFontSize
                        )
                          .toFixed(3)
                          .toString();
                        updateHeroHeaderField("margin", {
                          top: convertedValue,
                          right: convertedValue,
                          bottom: convertedValue,
                          left: convertedValue,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.margin?.linked ?? false,
                        });
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-2 w-full">
                  <SatoriDimensionsBox
                    label={"Top"}
                    value={
                      cleanDimenValues(
                        selectedSlide.heroHeader?.margin?.top!
                      ) || ""
                    }
                    unit={selectedSlide.heroHeader?.margin?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroHeaderField("margin", {
                        ...selectedSlide.heroHeader?.margin,
                        top: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.right!) *
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.margin?.bottom!
                          ) * parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.margin?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.right!) /
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.margin?.bottom!
                          ) / parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.margin?.linked ?? false,
                        });
                      }
                    }}
                  />
                  <SatoriDimensionsBox
                    label={"Right"}
                    value={
                      cleanDimenValues(
                        selectedSlide.heroHeader?.margin?.right!
                      ) || ""
                    }
                    unit={selectedSlide.heroHeader?.margin?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroHeaderField("margin", {
                        ...selectedSlide.heroHeader?.margin!,
                        right: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.right!) *
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.margin?.bottom!
                          ) * parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.margin?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.right!) /
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.margin?.bottom!
                          ) / parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.margin?.linked ?? false,
                        });
                      }
                    }}
                  />
                  <SatoriDimensionsBox
                    label={"Bottom"}
                    value={
                      cleanDimenValues(
                        selectedSlide.heroHeader?.margin?.bottom!
                      ) || ""
                    }
                    unit={selectedSlide.heroHeader?.margin?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroHeaderField("margin", {
                        ...selectedSlide.heroHeader?.margin!,
                        bottom: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.right!) *
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.margin?.bottom!
                          ) * parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.margin?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.right!) /
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.margin?.bottom!
                          ) / parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.margin?.linked ?? false,
                        });
                      }
                    }}
                  />
                  <SatoriDimensionsBox
                    label={"Left"}
                    value={
                      cleanDimenValues(
                        selectedSlide.heroHeader?.margin?.left!
                      ) || ""
                    }
                    unit={selectedSlide.heroHeader?.margin?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroHeaderField("margin", {
                        ...selectedSlide.heroHeader?.margin!,
                        left: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.right!) *
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.margin?.bottom!
                          ) * parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.margin?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.right!) /
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.margin?.bottom!
                          ) / parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.margin?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.margin?.linked ?? false,
                        });
                      }
                    }}
                  />
                </div>
              )}

              {/* Hero Header Padding */}
              <div className="flex items-center gap-2 my-2 w-full justify-between">
                <span className="text-base">Padding</span>
                <button
                  onClick={() =>
                    updateHeroHeaderField("padding", {
                      ...selectedSlide.heroHeader?.padding,
                      linked: !selectedSlide.heroHeader?.padding?.linked,
                    })
                  }
                  className="ml-1 p-1"
                  title={
                    selectedSlide.heroHeader?.padding?.linked
                      ? "Unlink sides"
                      : "Link all sides"
                  }
                >
                  {selectedSlide.heroHeader?.padding?.linked ? (
                    <Link size={16} />
                  ) : (
                    <Unlink size={16} />
                  )}
                </button>
              </div>
              {selectedSlide.heroHeader?.padding?.linked ? (
                <div className="flex flex-col gap-2">
                  <SatoriDimensionsBox
                    value={
                      cleanDimenValues(
                        selectedSlide.heroHeader?.padding?.left!
                      ) || ""
                    }
                    unit={selectedSlide.heroHeader?.padding?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroHeaderField("padding", {
                        ...selectedSlide.heroHeader?.padding!,
                        top: val,
                        right: val,
                        bottom: val,
                        left: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedValue = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.top!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("padding", {
                          top: convertedValue,
                          right: convertedValue,
                          bottom: convertedValue,
                          left: convertedValue,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.padding?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedValue = (
                          parseFloat(selectedSlide.heroHeader?.padding?.top!) /
                          parentRawFontSize
                        )
                          .toFixed(3)
                          .toString();
                        updateHeroHeaderField("padding", {
                          top: convertedValue,
                          right: convertedValue,
                          bottom: convertedValue,
                          left: convertedValue,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.padding?.linked ?? false,
                        });
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-2 w-full">
                  <SatoriDimensionsBox
                    label={"Top"}
                    value={
                      cleanDimenValues(
                        selectedSlide.heroHeader?.padding?.top!
                      ) || ""
                    }
                    unit={selectedSlide.heroHeader?.padding?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroHeaderField("padding", {
                        ...selectedSlide.heroHeader?.padding!,
                        top: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.right!
                          ) * parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.bottom!
                          ) * parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.padding?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.right!
                          ) / parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.bottom!
                          ) / parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.padding?.linked ?? false,
                        });
                      }
                    }}
                  />
                  <SatoriDimensionsBox
                    label={"Right"}
                    value={
                      cleanDimenValues(
                        selectedSlide.heroHeader?.padding?.right!
                      ) || ""
                    }
                    unit={selectedSlide.heroHeader?.padding?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroHeaderField("padding", {
                        ...selectedSlide.heroHeader?.padding!,
                        right: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.right!
                          ) * parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.bottom!
                          ) * parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.padding?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.right!
                          ) / parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.bottom!
                          ) / parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.padding?.linked ?? false,
                        });
                      }
                    }}
                  />
                  <SatoriDimensionsBox
                    label={"Bottom"}
                    value={
                      cleanDimenValues(
                        selectedSlide.heroHeader?.padding?.bottom!
                      ) || ""
                    }
                    unit={selectedSlide.heroHeader?.padding?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroHeaderField("padding", {
                        ...selectedSlide.heroHeader?.padding!,
                        bottom: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.right!
                          ) * parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.bottom!
                          ) * parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.padding?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.right!
                          ) / parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.bottom!
                          ) / parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.padding?.linked ?? false,
                        });
                      }
                    }}
                  />
                  <SatoriDimensionsBox
                    label={"Left"}
                    value={
                      cleanDimenValues(
                        selectedSlide.heroHeader?.padding?.left!
                      ) || ""
                    }
                    unit={selectedSlide.heroHeader?.padding?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroHeaderField("padding", {
                        ...selectedSlide.heroHeader?.padding!,
                        left: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.right!
                          ) * parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.bottom!
                          ) * parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.padding?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.right!
                          ) / parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(
                            selectedSlide.heroHeader?.padding?.bottom!
                          ) / parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroHeader?.padding?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroHeader?.padding?.linked ?? false,
                        });
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </SatoriCollapsiblePanel>
          <SatoriCollapsiblePanel
            icon={<Text size={18} />}
            title={"Hero Text"}
            defaultOpen={false}
          >
            <div className="flex flex-col gap-3">
              <SatoriTextBox
                label={"Text"}
                value={selectedSlide.heroText?.text || ""}
                onChange={(val) => updateHeroTextField("text", val)}
              />
              <SatoriDropDownList
                label={"Font Family"}
                isFontFamily={true}
                value={
                  selectedSlide.heroText?.fontFamily || "Inter, sans-serif"
                }
                onChange={(val) => updateHeroTextField("fontFamily", val)}
              />
              <SatoriDimensionsBox
                label={"Font Size"}
                value={
                  cleanDimenValues(selectedSlide.heroText?.fontSize?.value!) ||
                  ""
                }
                unit={selectedSlide.heroText?.fontSize?.unit || "px"}
                units={NOPERCENT_UNITS}
                onValueChange={(val) =>
                  updateHeroTextField("fontSize", {
                    value: val,
                    unit: selectedSlide.heroText?.fontSize?.unit || "rem",
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    updateHeroTextField("fontSize", {
                      value:
                        Math.round(
                          parseFloat(selectedSlide.heroText?.fontSize?.value!) *
                            parentRawFontSize
                        ).toString() || "",
                      unit: unitVal,
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    updateHeroTextField("fontSize", {
                      value:
                        (
                          parseFloat(selectedSlide.heroText?.fontSize?.value!) /
                          parentRawFontSize
                        ).toString() || "",
                      unit: unitVal,
                    });
                  }
                }}
              />

              <SatoriColorPicker
                label={"Text Color"}
                value={selectedSlide.heroText?.color || "#ffffff"}
                onChange={(val) => updateHeroTextField("color", val)}
              />

              <SatoriDimensionsBox
                label={"Line Height"}
                value={
                  cleanDimenValues(
                    selectedSlide.heroText?.lineHeight?.value!
                  ) || ""
                }
                unit={selectedSlide.heroText?.lineHeight?.unit || "px"}
                units={NOPERCENT_UNITS}
                onValueChange={(val) =>
                  updateHeroTextField("lineHeight", {
                    value: val,
                    unit: selectedSlide.heroText?.lineHeight?.unit || "rem",
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    updateHeroTextField("lineHeight", {
                      value:
                        Math.round(
                          parseFloat(
                            selectedSlide.heroText?.lineHeight?.value!
                          ) * parentRawFontSize
                        ).toString() || "",
                      unit: unitVal,
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    updateHeroTextField("lineHeight", {
                      value:
                        (
                          parseFloat(
                            selectedSlide.heroText?.lineHeight?.value!
                          ) / parentRawFontSize
                        ).toString() || "",
                      unit: unitVal,
                    });
                  }
                }}
              />

              <SatoriDropDownList
                layout={"horizontal"}
                label={"Font Weight"}
                value={selectedSlide.heroText?.fontWeight ?? "700"}
                isFontFamily={false}
                options={FONT_WEIGHTS}
                onChange={(val) => updateHeroTextField("fontWeight", val)}
              />

              <SatoriDropDownList
                layout={"horizontal"}
                label={"Text Decoration"}
                value={selectedSlide.heroText?.textDecoration ?? "none"}
                isFontFamily={false}
                options={TEXT_DECORATIONS}
                onChange={(val) => updateHeroTextField("fontWeight", val)}
              />

              <SatoriDropDownList
                label={"Text Transformation"}
                value={selectedSlide.heroText?.textTransform ?? "none"}
                isFontFamily={false}
                options={TEXT_TRANSFORMATIONS}
                onChange={(val) => updateHeroTextField("textTransform", val)}
              />

              <SatoriDimensionsBox
                label={"Letter Spacing"}
                value={
                  cleanDimenValues(
                    selectedSlide.heroText?.letterSpacing?.value!
                  ) || "0"
                }
                unit={selectedSlide.heroText?.letterSpacing?.unit || "px"}
                units={NOPERCENT_UNITS}
                onValueChange={(val) =>
                  updateHeroTextField("letterSpacing", {
                    value: val,
                    unit: selectedSlide.heroText?.letterSpacing?.unit || "rem",
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    updateHeroTextField("letterSpacing", {
                      value:
                        Math.round(
                          parseFloat(
                            selectedSlide.heroText?.letterSpacing?.value!
                          ) * parentRawFontSize
                        ).toString() || "",
                      unit: unitVal,
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    updateHeroTextField("letterSpacing", {
                      value:
                        (
                          parseFloat(
                            selectedSlide.heroText?.letterSpacing?.value!
                          ) / parentRawFontSize
                        ).toString() || "",
                      unit: unitVal,
                    });
                  }
                }}
              />

              {/* Hero Text Margins */}
              <div className="flex items-center gap-2 my-2 w-full justify-between">
                <span className="text-base">Margins</span>
                <button
                  onClick={() =>
                    updateHeroTextField("margin", {
                      ...selectedSlide.heroText?.margin,
                      linked: !selectedSlide.heroText?.margin?.linked,
                    })
                  }
                  className="ml-1 p-1"
                  title={
                    selectedSlide.heroText?.margin?.linked
                      ? "Unlink sides"
                      : "Link all sides"
                  }
                >
                  {selectedSlide.heroText?.margin?.linked ? (
                    <Link size={16} />
                  ) : (
                    <Unlink size={16} />
                  )}
                </button>
              </div>
              {selectedSlide.heroText?.margin?.linked ? (
                <div className="flex flex-col gap-2">
                  <SatoriDimensionsBox
                    value={
                      cleanDimenValues(selectedSlide.heroText?.margin?.left!) ||
                      ""
                    }
                    unit={selectedSlide.heroText?.margin?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroTextField("margin", {
                        ...selectedSlide.heroText?.margin,
                        top: val,
                        right: val,
                        bottom: val,
                        left: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedValue = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.top!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("margin", {
                          top: convertedValue,
                          right: convertedValue,
                          bottom: convertedValue,
                          left: convertedValue,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.margin?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedValue = (
                          parseFloat(selectedSlide.heroText?.margin?.top!) /
                          parentRawFontSize
                        )
                          .toFixed(3)
                          .toString();
                        updateHeroTextField("margin", {
                          top: convertedValue,
                          right: convertedValue,
                          bottom: convertedValue,
                          left: convertedValue,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.margin?.linked ?? false,
                        });
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-2 w-full">
                  <SatoriDimensionsBox
                    label={"Top"}
                    value={
                      cleanDimenValues(selectedSlide.heroText?.margin?.top!) ||
                      ""
                    }
                    unit={selectedSlide.heroText?.margin?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroTextField("margin", {
                        ...selectedSlide.heroText?.margin,
                        top: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.right!) *
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.bottom!) *
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.margin?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.right!) /
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.bottom!) /
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.margin?.linked ?? false,
                        });
                      }
                    }}
                  />
                  <SatoriDimensionsBox
                    label={"Right"}
                    value={
                      cleanDimenValues(
                        selectedSlide.heroText?.margin?.right!
                      ) || ""
                    }
                    unit={selectedSlide.heroText?.margin?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroTextField("margin", {
                        ...selectedSlide.heroText?.margin,
                        right: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.right!) *
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.bottom!) *
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.margin?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.right!) /
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.bottom!) /
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.margin?.linked ?? false,
                        });
                      }
                    }}
                  />
                  <SatoriDimensionsBox
                    label={"Bottom"}
                    value={
                      cleanDimenValues(
                        selectedSlide.heroText?.margin?.bottom!
                      ) || ""
                    }
                    unit={selectedSlide.heroText?.margin?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroTextField("margin", {
                        ...selectedSlide.heroText?.margin,
                        bottom: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.right!) *
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.bottom!) *
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.margin?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.right!) /
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.bottom!) /
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.margin?.linked ?? false,
                        });
                      }
                    }}
                  />
                  <SatoriDimensionsBox
                    label={"Left"}
                    value={
                      cleanDimenValues(selectedSlide.heroText?.margin?.left!) ||
                      ""
                    }
                    unit={selectedSlide.heroText?.margin?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroTextField("margin", {
                        ...selectedSlide.heroText?.margin,
                        left: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.right!) *
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.bottom!) *
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.margin?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.right!) /
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.bottom!) /
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.margin?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("margin", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.margin?.linked ?? false,
                        });
                      }
                    }}
                  />
                </div>
              )}

              {/* Hero Text Padding */}
              <div className="flex items-center gap-2 my-2 w-full justify-between">
                <span className="text-base">Padding</span>
                <button
                  onClick={() =>
                    updateHeroTextField("padding", {
                      ...selectedSlide.heroText?.padding,
                      linked: !selectedSlide.heroText?.padding?.linked,
                    })
                  }
                  className="ml-1 p-1"
                  title={
                    selectedSlide.heroText?.padding?.linked
                      ? "Unlink sides"
                      : "Link all sides"
                  }
                >
                  {selectedSlide.heroText?.padding?.linked ? (
                    <Link size={16} />
                  ) : (
                    <Unlink size={16} />
                  )}
                </button>
              </div>
              {selectedSlide.heroText?.padding?.linked ? (
                <div className="flex flex-col gap-2">
                  <SatoriDimensionsBox
                    value={
                      cleanDimenValues(
                        selectedSlide.heroText?.padding?.left!
                      ) || ""
                    }
                    unit={selectedSlide.heroText?.padding?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroTextField("padding", {
                        ...selectedSlide.heroText?.padding!,
                        top: val,
                        right: val,
                        bottom: val,
                        left: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedValue = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.top!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("padding", {
                          top: convertedValue,
                          right: convertedValue,
                          bottom: convertedValue,
                          left: convertedValue,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.padding?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedValue = (
                          parseFloat(selectedSlide.heroText?.padding?.top!) /
                          parentRawFontSize
                        )
                          .toFixed(3)
                          .toString();
                        updateHeroTextField("padding", {
                          top: convertedValue,
                          right: convertedValue,
                          bottom: convertedValue,
                          left: convertedValue,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.padding?.linked ?? false,
                        });
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-2 w-full">
                  <SatoriDimensionsBox
                    label={"Top"}
                    value={
                      cleanDimenValues(selectedSlide.heroText?.padding?.top!) ||
                      ""
                    }
                    unit={selectedSlide.heroText?.padding?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroTextField("padding", {
                        ...selectedSlide.heroText?.padding,
                        top: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.right!) *
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.bottom!) *
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.padding?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.right!) /
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.bottom!) /
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.padding?.linked ?? false,
                        });
                      }
                    }}
                  />
                  <SatoriDimensionsBox
                    label={"Right"}
                    value={
                      cleanDimenValues(
                        selectedSlide.heroText?.padding?.right!
                      ) || ""
                    }
                    unit={selectedSlide.heroText?.padding?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroTextField("padding", {
                        ...selectedSlide.heroText?.padding,
                        right: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.right!) *
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.bottom!) *
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.padding?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.right!) /
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.bottom!) /
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.padding?.linked ?? false,
                        });
                      }
                    }}
                  />
                  <SatoriDimensionsBox
                    label={"Bottom"}
                    value={
                      cleanDimenValues(
                        selectedSlide.heroText?.padding?.bottom!
                      ) || ""
                    }
                    unit={selectedSlide.heroText?.padding?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroTextField("padding", {
                        ...selectedSlide.heroText?.padding,
                        bottom: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.right!) *
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.bottom!) *
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.padding?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.right!) /
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.bottom!) /
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.padding?.linked ?? false,
                        });
                      }
                    }}
                  />
                  <SatoriDimensionsBox
                    label={"Left"}
                    value={
                      cleanDimenValues(
                        selectedSlide.heroText?.padding?.left!
                      ) || ""
                    }
                    unit={selectedSlide.heroText?.padding?.unit || "px"}
                    units={NOPERCENT_UNITS}
                    onValueChange={(val) =>
                      updateHeroTextField("padding", {
                        ...selectedSlide.heroHeader?.padding,
                        left: val,
                      })
                    }
                    onUnitChange={(unitVal) => {
                      if (unitVal === "px") {
                        // We need to change from rem/em value to pixel value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.top!) *
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.right!) *
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.bottom!) *
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.left!) *
                            parentRawFontSize
                        ).toString();
                        updateHeroHeaderField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.padding?.linked ?? false,
                        });
                      } else if (unitVal === "rem" || unitVal === "em") {
                        // We need to change from pixel value to rem/em value
                        const convertedTop = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.top!) /
                            parentRawFontSize
                        ).toString();
                        const convertedRight = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.right!) /
                            parentRawFontSize
                        ).toString();
                        const convertedBottom = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.bottom!) /
                            parentRawFontSize
                        ).toString();
                        const convertedLeft = Math.round(
                          parseFloat(selectedSlide.heroText?.padding?.left!) /
                            parentRawFontSize
                        ).toString();
                        updateHeroTextField("padding", {
                          top: convertedTop,
                          right: convertedRight,
                          bottom: convertedBottom,
                          left: convertedLeft,
                          unit: unitVal,
                          linked:
                            selectedSlide.heroText?.padding?.linked ?? false,
                        });
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </SatoriCollapsiblePanel>
          <SatoriCollapsiblePanel
            icon={<SignpostBig size={18} />}
            title={"Call to Action"}
            defaultOpen={false}
          >
            <div className="flex flex-col gap-3">
              <SatoriTextBox
                type={"Button Text"}
                value={selectedSlide.cta?.text ?? ""}
                onChange={(val) => updateCtaField("text", val)}
                placeholder="Button Label"
              />

              <SatoriTextBox
                type={"Button Text"}
                value={selectedSlide.cta?.url ?? ""}
                onChange={(val) => updateCtaField("url", val)}
                placeholder="https://www.example.com"
              />

              <SatoriCheckBox
                checked={!!selectedSlide.cta?.openInNewTab}
                onChange={(val) => updateCtaField("openInNewTab", val)}
                label={"Open in new tab"}
              />

              <SatoriDropDownList
                label={"Alignment"}
                layout="horizontal"
                options={ALIGN_OPTIONS}
                isFontFamily={false}
                value={selectedSlide.cta?.alignment || "center"}
                onChange={(val) => updateCtaField("alignment", val)}
              />
            </div>
          </SatoriCollapsiblePanel>
        </>
      )}
    </section>
  );
});
